/**
 * The skill-type vocabulary.
 *
 * The ids are `agent_skills.type_id`, a foreign key onto `agent_skill_types.id` in Apollo.
 * They are the contract with the backend and never change:
 *
 *   1  string  free text
 *   2  int     a decimal literal, stored as text
 *   3  bool    "0" or "1", stored as text
 *   4  enum    one of the skill's own declared values, stored as text
 *
 * Every value ends up as a TaskRouter worker attribute keyed by the skill name, and a queue's
 * target expression matches on that attribute literally. That is why each type has exactly one
 * canonical spelling - a bool is "0"/"1", never "true"/"no"/"Yes" - and why the screen refuses
 * to store anything else.
 *
 * Apollo owns the `agent_skill_types` table and can grow it before this app is redeployed, so
 * an id missing from this table is NOT a bug to assert on. Callers must treat it as
 * *unrecognised*: fall back to a plain text box and say so on screen. Rendering
 * `CustomSkillsTypes[id]` straight into JSX gives `undefined` for an unknown id, which is the
 * one thing that must not happen.
 */

export const SKILL_TYPE_STRING = 1;
export const SKILL_TYPE_INT = 2;
export const SKILL_TYPE_BOOL = 3;
export const SKILL_TYPE_ENUM = 4;

interface SkillType {
    [key: number]: string;
}

export const CustomSkillsTypes: SkillType = []
CustomSkillsTypes[SKILL_TYPE_STRING] = "string"
CustomSkillsTypes[SKILL_TYPE_INT] = "int"
CustomSkillsTypes[SKILL_TYPE_BOOL] = "bool"
CustomSkillsTypes[SKILL_TYPE_ENUM] = "enum"

/** True when this build knows how to edit values of that type. */
export function IsKnownSkillType(typeId: number): boolean {
    return CustomSkillsTypes[typeId] !== undefined
}

/** Never returns undefined - safe to render. */
export function SkillTypeName(typeId: number): string {
    const name = CustomSkillsTypes[typeId]
    return (name === undefined) ? 'unrecognised type #' + typeId : name
}
